import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Spinner from "../../components/Spinner/Spinner";
import { UserContext } from "../../utils/contexts/UserContext";
import { hasPermission, TEMPLATE_PERMISSION_CODES } from "../../utils/permissions";
import {
  getActivationCarry,
  getAnswerOptionSets,
  getRubricVersion,
  publishVersion,
  setActivation,
  startDraft,
  updateDraftContent,
  updateTemplate,
} from "../../services/rubricService";
import ConfirmModal from "./ConfirmModal";
import { LanguageTabs, TranslationReference } from "./LocalizedFields";
import QuestionConditions from "./QuestionConditions";
import SubjectScopeField, { SubjectNowRequired } from "./SubjectScopeField";
import TemplatePreview from "./TemplatePreview";
import WordingEditor from "./WordingEditor";
import { formatApiError } from "../../utils/helpers/apiErrors";
import { editorScopeLabel } from "../../utils/helpers/standardScope";
import { confirmLeaveUnsavedWork, guardUnsavedWork } from "../../utils/helpers/unsavedWork";
import useDomainSubjects from "../../utils/hooks/useDomainSubjects";
import {
  apiTreeToEditorState,
  arrayMove,
  buildContentPayload,
  computeQuestionDepths,
  countConditions,
  displayText,
  emptyQuestion,
  emptyStandard,
  emptyText,
  findConditionCycle,
  hasText,
  isScopeMissingSubjects,
  sameText,
  textFromApi,
  translationCoverage,
  trimText,
  validateEditorState,
} from "./templateTree";

const inputBase =
  "bg-slate-50 border border-slate-200 rounded-xl text-slate-800 focus:bg-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 outline-none transition";

function IconButton({ title, onClick, disabled, danger = false, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      onClick={onClick}
      disabled={disabled}
      className={`p-1.5 rounded-lg transition disabled:opacity-30 disabled:cursor-not-allowed ${
        danger
          ? "text-slate-400 hover:text-rose-600 hover:bg-rose-50"
          : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"
      }`}
    >
      {children}
    </button>
  );
}

const ArrowUpIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
  </svg>
);
const ArrowDownIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
  </svg>
);
const TrashIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);

export default function TemplateEditor() {
  const { t, i18n } = useTranslation();
  const { uuid } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  const [version, setVersion] = useState(null);
  const [optionSets, setOptionSets] = useState([]);
  const [standards, setStandards] = useState([]);
  const [templateName, setTemplateName] = useState(emptyText);
  // Which language the fields currently hold. Editor-local on purpose: driving
  // it through i18n.changeLanguage would remount the routed tree (App.js keys
  // <Routes> on the UI language) and silently discard the unsaved draft.
  const [authorLang, setAuthorLang] = useState(
    () => i18n.resolvedLanguage || "kk",
  );
  const [showReference, setShowReference] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set([0]));
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [publishModal, setPublishModal] = useState(false);
  const [publishAndActivate, setPublishAndActivate] = useState(true);
  const [carryPlan, setCarryPlan] = useState(null);
  const [carryFailed, setCarryFailed] = useState(false);
  // `editing_mode` says which surface to OPEN; it is not a restriction. A
  // published version takes structural edits too — the only refusal is dropping
  // a row somebody was scored against — so the author can switch either way.
  const [surfaceOverride, setSurfaceOverride] = useState(null);
  const [isPublishing, setIsPublishing] = useState(false);
  const [invalidHighlight, setInvalidHighlight] = useState(false);
  const [cycleKeys, setCycleKeys] = useState(null); // Set of question keys on a condition loop
  const [openDraftBusy, setOpenDraftBusy] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const canManage = hasPermission(user, TEMPLATE_PERMISSION_CODES.MANAGE);
  // Seeded with the version's own subjects, so a stored scope is named even
  // before (or without) the catalog.
  const storedSubjects = useMemo(
    () => (version?.standards || []).flatMap((std) => std.subjects || []),
    [version],
  );
  const subjectCatalog = useDomainSubjects({ enabled: canManage, seed: storedSubjects });
  const isDraft = version?.status === "draft";
  // The server decides what may be edited — it knows the caller's write scope
  // and whether anything has been observed against this version. "full" is a
  // draft or an unused published version; "wording" is text-only.
  const editingMode = version?.editing_mode || "readonly";
  // `readonly` is the one real gate and is never overridable.
  const surface = editingMode === "readonly" ? "readonly" : surfaceOverride || editingMode;

  const applyVersion = useCallback((data) => {
    setVersion(data);
    setTemplateName(textFromApi(data?.template, "name"));
    const tree = apiTreeToEditorState(data);
    // A brand-new blank draft gets a starter standard so the author is never
    // staring at an empty page.
    setStandards(
      tree.length > 0 || data?.status !== "draft" ? tree : [emptyStandard()],
    );
  }, []);

  useEffect(() => {
    if (!canManage) return undefined;
    const controller = new AbortController();
    setIsLoading(true);
    setLoadError(false);
    setIsDirty(false);
    setShowPreview(false);
    setOpenDraftBusy(false);
    Promise.all([
      getRubricVersion({ uuid, signal: controller.signal }),
      getAnswerOptionSets({ signal: controller.signal }),
    ])
      .then(([versionRes, setsRes]) => {
        applyVersion(versionRes);
        setOptionSets(setsRes?.results ?? setsRes ?? []);
        setExpanded(new Set([0]));
        setIsLoading(false);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || err?.name === "CanceledError") return;
        setLoadError(true);
        setIsLoading(false);
      });
    return () => controller.abort();
  }, [uuid, canManage, applyVersion, reloadKey]);

  // Re-reads the version without the loading gate, so the wording editor stays
  // mounted and keeps every correction typed into it.
  const refreshVersion = () =>
    getRubricVersion({ uuid })
      .then(applyVersion)
      .catch(() => toast.error(t("templates.editorLoadError")));

  // Warn about unsaved draft changes on hard navigation (reload / close tab).
  useEffect(() => guardUnsavedWork(isDirty), [isDirty]);

  const mutate = (updater) => {
    setStandards(updater);
    setIsDirty(true);
    setCycleKeys(null);
  };

  const updateStandard = (index, patch) =>
    mutate((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  const updateQuestion = (stdIndex, qIndex, patch) =>
    mutate((prev) =>
      prev.map((s, i) =>
        i === stdIndex
          ? {
              ...s,
              questions: s.questions.map((q, j) =>
                j === qIndex ? { ...q, ...patch } : q,
              ),
            }
          : s,
      ),
    );

  const moveStandard = (index, delta) => {
    mutate((prev) => arrayMove(prev, index, index + delta));
    setExpanded((prev) => {
      const next = new Set();
      prev.forEach((i) => {
        if (i === index) next.add(index + delta);
        else if (i === index + delta) next.add(index);
        else next.add(i);
      });
      return next;
    });
  };

  // Strip conditions that reference questions no longer in the tree.
  const dropDanglingConditions = (stds) => {
    const keys = new Set(stds.flatMap((s) => s.questions.map((q) => q.key)));
    return stds.map((s) => ({
      ...s,
      questions: s.questions.map((q) => ({
        ...q,
        conditions: (q.conditions || []).filter((c) => keys.has(c.dependsOnKey)),
      })),
    }));
  };

  // Apply a deletion and surface how many conditions on *other* questions
  // silently lost their gate because of it.
  const applyDeletion = (next) => {
    const cleaned = dropDanglingConditions(next);
    if (countConditions(cleaned) < countConditions(next)) {
      toast.info(t("templates.conditionsDropped"));
    }
    mutate(() => cleaned);
  };

  const removeStandard = (index) => {
    applyDeletion(standards.filter((_, i) => i !== index));
    setExpanded((prev) => {
      const next = new Set();
      prev.forEach((i) => {
        if (i === index) return;
        next.add(i > index ? i - 1 : i);
      });
      return next;
    });
  };

  const removeQuestion = (stdIndex, qIndex) =>
    applyDeletion(
      standards.map((s, i) =>
        i === stdIndex
          ? { ...s, questions: s.questions.filter((_, j) => j !== qIndex) }
          : s,
      ),
    );

  const addStandard = () => {
    mutate((prev) => [...prev, emptyStandard()]);
    setExpanded((prev) => new Set([...prev, standards.length]));
  };

  const toggleExpanded = (index) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  // Shared by save + publish: highlight what's wrong, including the questions
  // on a condition loop.
  const reportValidationProblem = (problem) => {
    setInvalidHighlight(true);
    setExpanded(new Set(standards.map((_, i) => i)));
    if (problem === "templates.conditionCycle") {
      const cycle = findConditionCycle(standards);
      setCycleKeys(cycle ? new Set(cycle) : null);
    }
    toast.error(t(problem));
  };

  const saveDraft = async ({ silent = false } = {}) => {
    const problem = validateEditorState(standards);
    if (problem) {
      reportValidationProblem(problem);
      return null;
    }
    setIsSaving(true);
    try {
      const nextName = trimText(templateName);
      if (
        hasText(nextName) &&
        !sameText(nextName, textFromApi(version?.template, "name"))
      ) {
        await updateTemplate({
          uuid: version.template.uuid,
          body: { name: nextName },
        });
      }
      const updated = await updateDraftContent({
        uuid: version.uuid,
        body: { standards: buildContentPayload(standards, optionSets) },
      });
      applyVersion(updated);
      setIsDirty(false);
      setInvalidHighlight(false);
      if (!silent) toast.success(t("templates.saved"));
      return updated;
    } catch (err) {
      toast.error(formatApiError(err, t("templates.saveFailed")));
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const openPublishModal = () => {
    const problem = validateEditorState(standards);
    if (problem) {
      reportValidationProblem(problem);
      return;
    }
    setPublishModal(true);
    // What "switch over" would actually move, resolved server-side: the schools
    // on earlier versions of this template that the caller may write to, plus
    // the ones inheriting those rows. Without the numbers the checkbox is a
    // promise nobody can check.
    setCarryPlan(null);
    setCarryFailed(false);
    getActivationCarry({ uuid: version.uuid })
      .then(setCarryPlan)
      .catch(() => setCarryFailed(true));
  };

  const doPublish = async () => {
    setIsPublishing(true);
    // Publish first. If it fails, report it and stay on the (still-draft) editor.
    try {
      if (isDirty) {
        const saved = await saveDraft({ silent: true });
        if (!saved) {
          setIsPublishing(false);
          return;
        }
      }
      await publishVersion({
        uuid: version.uuid,
        // Only switch other schools over once we know which ones those are and
        // there is something to move. Unknown (still loading, or the preview
        // failed) means the caller's own school is pinned below and nobody else
        // is touched — never a silent network-wide move nobody was shown.
        carryActivations: publishAndActivate && carryPlan?.moves?.length > 0,
      });
    } catch (err) {
      toast.error(formatApiError(err, t("templates.publishFailed")));
      setIsPublishing(false);
      return;
    }
    // Publish committed. Activation is a SEPARATE request/transaction — if it
    // fails the version is still published, so report that distinctly and still
    // leave the editor. Retrying publish here would hit the immutable-version
    // 400 (a published version can't be re-published), so never send the user back.
    try {
      // `carry_activations` moved the rows already pinned to an earlier version
      // of this template. A school with no row of its own (inheriting, or never
      // activated) is not among them, so the explicit self-activation this
      // dialog has always done still runs — it is an idempotent upsert, and the
      // server resolves which school that is, which the client cannot.
      if (publishAndActivate) {
        await setActivation({ body: { template_version: version.uuid } });
      }
      toast.success(
        publishAndActivate
          ? t("templates.publishedAndActivatedToast")
          : t("templates.publishedToast"),
      );
    } catch (err) {
      toast.error(formatApiError(err, t("templates.publishedActivateFailed")));
    }
    navigate("/templates");
  };

  // Every way off this surface asks first. Whichever editor holds unsaved work
  // — the draft tree or the wording editor — guards it with `beforeunload`,
  // and the shared probe hears either.
  const confirmLeave = () => confirmLeaveUnsavedWork(t("templates.leaveConfirm"));

  const goBack = () => {
    if (confirmLeave()) navigate("/templates");
  };

  const openDraftFromHere = async () => {
    if (!confirmLeave()) return;
    setOpenDraftBusy(true);
    try {
      const draft = await startDraft({ uuid: version.uuid });
      navigate(`/templates/${draft.uuid}`);
    } catch (err) {
      toast.error(formatApiError(err, t("templates.editFailed")));
      setOpenDraftBusy(false);
    }
  };

  // Confirmed, the tree edits are dropped: a wording or scope save there would
  // replace the tree anyway, and a stale isDirty would keep asking.
  const openWordingSurface = () => {
    if (!confirmLeave()) return;
    applyVersion(version);
    setIsDirty(false);
    setSurfaceOverride("wording");
  };

  // Document-order index of every question — feeds the condition picker
  // (earlier questions only) and the condition chips' labels. Memoized so
  // renders not caused by tree edits (toggles, busy flags) skip the rebuild.
  const { questionIndex, labelByKey, setByKey } = useMemo(() => {
    const index = standards.flatMap((std, si) =>
      std.questions.map((q, qi) => ({
        key: q.key,
        label: `${si + 1}.${qi + 1} ${
          displayText(q.text, authorLang).trim().slice(0, 40) || "…"
        }`,
        optionSet: q.optionSet,
        si,
        qi,
      })),
    );
    return {
      questionIndex: index,
      labelByKey: Object.fromEntries(index.map((e) => [e.key, e.label])),
      setByKey: Object.fromEntries(index.map((e) => [e.key, e.optionSet])),
    };
  }, [standards, authorLang]);

  // Per-language progress over the strings a publish actually requires.
  const coverage = useMemo(
    () => translationCoverage(standards, templateName),
    [standards, templateName],
  );

  // Each standard's scope as saved, so on a published version the tree's scope
  // edits say what they do to past lessons, as the wording editor's do.
  const storedScopes = useMemo(
    () =>
      version && !isDraft
        ? new Map(
            version.standards.map((std) => [
              std.uuid,
              { subjectScope: std.subject_scope, subjects: std.subjects.map((s) => s.uuid) },
            ]),
          )
        : null,
    [version, isDraft],
  );

  if (isLoading) {
    return (
      <div className="flex-1 bg-slate-50">
        <Spinner label={t("templates.editorLoading")} className="py-24" />
      </div>
    );
  }

  if (loadError || !version) {
    return (
      <div className="flex-1 bg-slate-50 p-6">
        <div className="max-w-4xl mx-auto py-24 text-center space-y-4">
          <p className="text-sm text-rose-600 font-semibold">{t("templates.editorLoadError")}</p>
          <div className="flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => setReloadKey((k) => k + 1)}
              className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition"
            >
              {t("templates.retry")}
            </button>
            <button
              type="button"
              onClick={() => navigate("/templates")}
              className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:border-slate-400 transition"
            >
              {t("templates.backToTemplates")}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const backButton = (
    <button
      type="button"
      onClick={goBack}
      className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-500 hover:text-slate-800 transition"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 19l-7-7 7-7" />
      </svg>
      {t("templates.backToTemplates")}
    </button>
  );

  // Text-only mode: published AND already observed against. The structure is
  // what those observations were scored against, so only wording and a
  // standard's subjects may change — both retroactively, which the banner says
  // out loud.
  if (surface === "wording") {
    return (
      <div className="flex-1 bg-slate-50 p-6">
        <div className="max-w-4xl mx-auto space-y-5">
          <div className="space-y-3">
            {backButton}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                  {version.template?.name}
                </h1>
                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700">
                  {t("templates.versionN", { n: version.version_number })} ·{" "}
                  {t("templates.publishedChip")}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    if (confirmLeave()) setSurfaceOverride("full");
                  }}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 hover:border-slate-400 transition"
                >
                  {t("templates.editStructure")}
                </button>
                <button
                  type="button"
                  disabled={openDraftBusy}
                  onClick={openDraftFromHere}
                  className="px-4 py-2 rounded-xl border border-indigo-200 text-sm font-semibold text-indigo-600 hover:bg-indigo-50 transition disabled:opacity-60"
                >
                  {t("templates.newVersion")}
                </button>
              </div>
            </div>
          </div>
          <WordingEditor
            version={version}
            subjectCatalog={subjectCatalog}
            lang={authorLang}
            onLangChange={setAuthorLang}
            onSaved={(data) => {
              if (data) applyVersion(data);
              else refreshVersion();
            }}
          />
        </div>
      </div>
    );
  }

  // Read-only: a version outside our write scope (a child school's draft seen
  // from the head org, or a template we do not own). The editor must not open
  // on it — every save would 403.
  if (surface !== "full") {
    return (
      <div className="flex-1 bg-slate-50 p-6">
        <div className="max-w-4xl mx-auto space-y-5">
          <div className="space-y-3">
            {backButton}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="text-2xl font-black text-slate-900 tracking-tight">
                  {version.template?.name}
                </h1>
                <span className="px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700">
                  {t("templates.versionN", { n: version.version_number })} ·{" "}
                  {t("templates.publishedChip")}
                </span>
              </div>
              <button
                type="button"
                disabled={openDraftBusy}
                onClick={openDraftFromHere}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition disabled:opacity-60 shrink-0"
              >
                {version.can_edit ? t("templates.newVersion") : t("templates.customize")}
              </button>
            </div>
            <div className="px-4 py-3 rounded-xl bg-slate-100 border border-slate-200 text-xs font-semibold text-slate-600">
              {t("templates.readOnlyBanner")}
            </div>
          </div>
          <TemplatePreview
            standards={standards}
            optionSets={optionSets}
            subjectCatalog={subjectCatalog}
            lang={authorLang}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-slate-50 p-6">
      <div className="max-w-4xl mx-auto space-y-5">
        {/* Sticky toolbar */}
        <div className="sticky top-0 z-30 -mx-2 px-2 pt-1 pb-3 bg-slate-50/95 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-md border border-slate-200 p-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
              {backButton}
              <div className="flex items-center gap-2">
                {!showPreview && standards.length > 1 && (
                  <div className="hidden sm:flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setExpanded(new Set(standards.map((_, i) => i)))}
                      className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider whitespace-nowrap text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition"
                    >
                      {t("templates.expandAll")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setExpanded(new Set())}
                      className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider whitespace-nowrap text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition"
                    >
                      {t("templates.collapseAll")}
                    </button>
                  </div>
                )}
                <span
                  className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${
                    isDraft ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"
                  }`}
                >
                  {isDraft
                    ? t("templates.draftBadge", { n: version.version_number })
                    : t("templates.unusedPublishedBadge", { n: version.version_number })}
                </span>
                {isDirty && (
                  <span
                    className="w-2 h-2 rounded-full bg-amber-500"
                    title={t("templates.unsavedChanges")}
                  />
                )}
              </div>
            </div>

            {/* items-START, not center: with the reference lines showing, the
                name block grows downward and centring would drag Save/Publish
                halfway down the toolbar, away from the row they belong to. */}
            <div className="flex flex-col md:flex-row md:items-start gap-3">
              <div className="flex-1 min-w-0">
                <input
                  type="text"
                  value={templateName[authorLang] || ""}
                  onChange={(e) => {
                    setTemplateName({ ...templateName, [authorLang]: e.target.value });
                    setIsDirty(true);
                  }}
                  placeholder={t("templates.nameLabel")}
                  aria-label={t("templates.nameLabel")}
                  className={`w-full px-3 py-2 text-lg font-black tracking-tight ${inputBase}`}
                />
                {showReference && !showPreview && (
                  <TranslationReference
                    value={templateName}
                    lang={authorLang}
                    onCopy={(from) => {
                      setTemplateName({ ...templateName, [authorLang]: templateName[from] });
                      setIsDirty(true);
                    }}
                  />
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-lg">
                  {[
                    { v: false, label: t("templates.editMode") },
                    { v: true, label: t("templates.previewMode") },
                  ].map((opt) => (
                    <button
                      key={String(opt.v)}
                      type="button"
                      onClick={() => setShowPreview(opt.v)}
                      className={`px-3 py-1 rounded-md text-[11px] font-bold uppercase tracking-wider transition ${
                        showPreview === opt.v
                          ? "bg-white text-indigo-600 shadow-sm"
                          : "text-slate-500 hover:text-slate-700"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => saveDraft()}
                  disabled={isSaving || !isDirty}
                  className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 whitespace-nowrap hover:border-slate-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSaving
                    ? t("common.saving")
                    : isDraft
                      ? t("templates.saveDraft")
                      : t("common.save")}
                </button>
                {!isDraft && (
                  <button
                    type="button"
                    onClick={openWordingSurface}
                    className="px-4 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 whitespace-nowrap hover:border-slate-400 transition"
                  >
                    {t("templates.correctWording")}
                  </button>
                )}
                {isDraft && (
                  <button
                    type="button"
                    onClick={openPublishModal}
                    disabled={isSaving || isPublishing}
                    className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold whitespace-nowrap shadow-lg shadow-indigo-900/10 hover:bg-indigo-700 transition disabled:opacity-60"
                  >
                    {t("templates.publish")}
                  </button>
                )}
              </div>
            </div>

            {/* Authoring language. Sits in the STICKY toolbar so it stays
                reachable at any scroll depth — on a long rubric the fields you
                are filling are nowhere near the top of the page. */}
            {!showPreview && (
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 pt-3 border-t border-slate-100">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 hidden sm:inline">
                    {t("templates.authoringLanguage")}
                  </span>
                  <LanguageTabs
                    value={authorLang}
                    onChange={setAuthorLang}
                    coverage={coverage}
                  />
                </div>
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showReference}
                    onChange={(e) => setShowReference(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-slate-300 text-indigo-600 focus:ring-2 focus:ring-indigo-500/20"
                  />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    {t("templates.showOtherLanguages")}
                  </span>
                </label>
              </div>
            )}
          </div>
          {!isDraft && <SubjectNowRequired stored={version.standards} standards={standards} />}
        </div>

        {/* A published version stays fully editable only while nothing has been
            observed against it — say so, because the same screen refuses
            structural edits the moment the first visit lands. */}
        {!isDraft && !showPreview && (
          <div className="px-4 py-3 rounded-xl bg-emerald-50 border border-emerald-200 text-xs font-semibold text-emerald-800 leading-relaxed">
            {t("templates.unusedPublishedBanner")}
          </div>
        )}

        {showPreview ? (
          <TemplatePreview
            standards={standards}
            optionSets={optionSets}
            subjectCatalog={subjectCatalog}
            lang={authorLang}
          />
        ) : (
          <>
            {standards.map((std, stdIdx) => {
              const isExpanded = expanded.has(stdIdx);
              const titleInvalid = invalidHighlight && !hasText(std.title);
              // Subquestion-style nesting: indent follows the condition chain.
              const depths = computeQuestionDepths(std);
              const scopeLabel = editorScopeLabel(t, std, subjectCatalog.nameOf);
              return (
                <div
                  key={std.key}
                  className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden"
                >
                  {/* Standard header */}
                  <div className="px-4 py-3 flex items-center gap-3 bg-slate-50/50">
                    <button
                      type="button"
                      onClick={() => toggleExpanded(stdIdx)}
                      className="p-1 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition"
                      aria-label={t(isExpanded ? "common.close" : "templates.edit")}
                    >
                      <svg
                        className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-90" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                    <span className="w-7 h-7 rounded-lg bg-indigo-600 text-white text-xs font-black flex items-center justify-center shrink-0">
                      {stdIdx + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <input
                        type="text"
                        value={std.title[authorLang] || ""}
                        onChange={(e) =>
                          updateStandard(stdIdx, {
                            title: { ...std.title, [authorLang]: e.target.value },
                          })
                        }
                        placeholder={t("templates.standardTitlePlaceholder")}
                        aria-label={t("templates.standardTitlePlaceholder")}
                        className={`w-full px-3 py-1.5 text-sm font-bold ${inputBase} ${
                          titleInvalid ? "border-rose-300 ring-2 ring-rose-500/20" : ""
                        }`}
                      />
                      {showReference && (
                        <TranslationReference
                          value={std.title}
                          lang={authorLang}
                          onCopy={(from) =>
                            updateStandard(stdIdx, {
                              title: { ...std.title, [authorLang]: std.title[from] },
                            })
                          }
                        />
                      )}
                    </div>
                    {!isExpanded && (
                      <span className="text-[11px] font-semibold text-slate-500 whitespace-nowrap">
                        {t("templates.questionsCount", { count: std.questions.length })}
                      </span>
                    )}
                    {!isExpanded && scopeLabel && (
                      <span className="hidden sm:inline max-w-[14rem] truncate px-2 py-0.5 rounded-md bg-indigo-50 text-[11px] font-semibold text-indigo-700">
                        {scopeLabel}
                      </span>
                    )}
                    <div className="flex items-center shrink-0">
                      <IconButton
                        title={t("templates.moveUp")}
                        onClick={() => moveStandard(stdIdx, -1)}
                        disabled={stdIdx === 0}
                      >
                        {ArrowUpIcon}
                      </IconButton>
                      <IconButton
                        title={t("templates.moveDown")}
                        onClick={() => moveStandard(stdIdx, 1)}
                        disabled={stdIdx === standards.length - 1}
                      >
                        {ArrowDownIcon}
                      </IconButton>
                      <IconButton
                        title={t("templates.deleteStandard")}
                        onClick={() => removeStandard(stdIdx)}
                        danger
                      >
                        {TrashIcon}
                      </IconButton>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="px-4 pb-4 pt-3 space-y-3 border-t border-slate-100">
                      <div>
                        <input
                          type="text"
                          value={std.description[authorLang] || ""}
                          onChange={(e) =>
                            updateStandard(stdIdx, {
                              description: {
                                ...std.description,
                                [authorLang]: e.target.value,
                              },
                            })
                          }
                          placeholder={t("templates.standardDescriptionPlaceholder")}
                          aria-label={t("templates.standardDescriptionPlaceholder")}
                          className={`w-full px-3 py-1.5 text-xs ${inputBase}`}
                        />
                        {showReference && (
                          <TranslationReference
                            value={std.description}
                            lang={authorLang}
                            onCopy={(from) =>
                              updateStandard(stdIdx, {
                                description: {
                                  ...std.description,
                                  [authorLang]: std.description[from],
                                },
                              })
                            }
                          />
                        )}
                      </div>

                      <SubjectScopeField
                        subjectScope={std.subjectScope}
                        subjects={std.subjects}
                        catalog={subjectCatalog}
                        stored={storedScopes?.get(std.uuid)}
                        invalid={invalidHighlight && isScopeMissingSubjects(std)}
                        onChange={(patch) => updateStandard(stdIdx, patch)}
                      />

                      {/* Questions */}
                      {std.questions.length === 0 && (
                        <div className="px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-xs font-semibold text-amber-700">
                          {t("templates.noQuestionsWarning")}
                        </div>
                      )}
                      <div className="space-y-2">
                        {std.questions.map((q, qIdx) => {
                          const textInvalid = invalidHighlight && !hasText(q.text);
                          const depth = depths[q.key] || 0;
                          const onCycle = cycleKeys?.has(q.key);
                          const earlier = questionIndex.filter(
                            (e) => e.si < stdIdx || (e.si === stdIdx && e.qi < qIdx),
                          );
                          return (
                            <div
                              key={q.key}
                              style={depth ? { marginLeft: depth * 24 } : undefined}
                              className={`p-2 rounded-xl border ${
                                onCycle ? "border-rose-300 ring-2 ring-rose-500/20" : "border-slate-100"
                              } ${depth ? "border-l-[3px] border-l-indigo-200" : ""}`}
                            >
                              <div className="flex items-start gap-2">
                                <span className="mt-2 text-[10px] font-black text-slate-500 w-9 shrink-0 text-right">
                                  {stdIdx + 1}.{qIdx + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                  <textarea
                                    rows={2}
                                    value={q.text[authorLang] || ""}
                                    onChange={(e) =>
                                      updateQuestion(stdIdx, qIdx, {
                                        text: { ...q.text, [authorLang]: e.target.value },
                                      })
                                    }
                                    placeholder={t("templates.questionPlaceholder")}
                                    aria-label={t("templates.questionPlaceholder")}
                                    className={`w-full px-3 py-2 text-sm resize-y min-h-[60px] ${inputBase} ${
                                      textInvalid ? "border-rose-300 ring-2 ring-rose-500/20" : ""
                                    }`}
                                  />
                                  {showReference && (
                                    <TranslationReference
                                      value={q.text}
                                      lang={authorLang}
                                      onCopy={(from) =>
                                        updateQuestion(stdIdx, qIdx, {
                                          text: { ...q.text, [authorLang]: q.text[from] },
                                        })
                                      }
                                    />
                                  )}
                                </div>
                                <label className="flex items-center gap-1.5 mt-2 shrink-0 cursor-pointer select-none">
                                  <input
                                    type="checkbox"
                                    checked={q.isRequired}
                                    onChange={(e) =>
                                      updateQuestion(stdIdx, qIdx, {
                                        isRequired: e.target.checked,
                                      })
                                    }
                                    className="accent-indigo-600"
                                  />
                                  <span className="text-[10px] font-bold text-slate-500 uppercase">
                                    {t("templates.required")}
                                  </span>
                                </label>
                                <div className="flex items-center mt-0.5 shrink-0">
                                  <IconButton
                                    title={t("templates.moveUp")}
                                    onClick={() =>
                                      updateStandard(stdIdx, {
                                        questions: arrayMove(std.questions, qIdx, qIdx - 1),
                                      })
                                    }
                                    disabled={qIdx === 0}
                                  >
                                    {ArrowUpIcon}
                                  </IconButton>
                                  <IconButton
                                    title={t("templates.moveDown")}
                                    onClick={() =>
                                      updateStandard(stdIdx, {
                                        questions: arrayMove(std.questions, qIdx, qIdx + 1),
                                      })
                                    }
                                    disabled={qIdx === std.questions.length - 1}
                                  >
                                    {ArrowDownIcon}
                                  </IconButton>
                                  <IconButton
                                    title={t("templates.deleteQuestion")}
                                    onClick={() => removeQuestion(stdIdx, qIdx)}
                                    danger
                                  >
                                    {TrashIcon}
                                  </IconButton>
                                </div>
                              </div>
                              <div className="pl-11">
                                <QuestionConditions
                                  conditions={q.conditions || []}
                                  earlier={earlier}
                                  preferredDefaultKey={qIdx > 0 ? std.questions[0].key : null}
                                  labelByKey={labelByKey}
                                  setByKey={setByKey}
                                  optionSets={optionSets}
                                  onChange={(conditions) =>
                                    updateQuestion(stdIdx, qIdx, { conditions })
                                  }
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          updateStandard(stdIdx, {
                            questions: [...std.questions, emptyQuestion()],
                          })
                        }
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-indigo-600 hover:bg-indigo-50 transition"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
                        </svg>
                        {t("templates.addQuestion")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}

            <button
              type="button"
              onClick={addStandard}
              className="w-full py-4 rounded-2xl border-2 border-dashed border-slate-300 text-sm font-bold text-slate-500 hover:border-indigo-400 hover:text-indigo-600 transition"
            >
              + {t("templates.addStandard")}
            </button>
          </>
        )}
      </div>

      {publishModal && (
        <ConfirmModal
          title={t("templates.publishConfirmTitle")}
          body={t("templates.publishConfirmBody", { n: version.version_number })}
          confirmLabel={isPublishing ? t("templates.publishing") : t("templates.publish")}
          busy={isPublishing}
          onConfirm={doPublish}
          onClose={() => setPublishModal(false)}
        >
          <label className="mt-4 flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:border-indigo-200 transition cursor-pointer">
            <input
              type="checkbox"
              checked={publishAndActivate}
              onChange={(e) => setPublishAndActivate(e.target.checked)}
              className="mt-0.5 accent-indigo-600"
            />
            <span className="text-sm font-semibold text-slate-700">
              {t("templates.publishAndActivate")}
              {/* A rubric owned by a head office is one row for a whole
                  network: say how many schools this moves BEFORE it moves
                  them, or the checkbox is a promise nobody can check. */}
              {carryPlan === null && !carryFailed && (
                <span className="mt-1 block text-xs font-medium text-slate-400">
                  {t("templates.carryChecking")}
                </span>
              )}
              {carryFailed && (
                <span className="mt-1 block text-xs font-medium text-amber-600">
                  {t("templates.carryUnknown")}
                </span>
              )}
              {carryPlan?.moves?.length > 0 && (
                <span className="mt-1 block text-xs font-medium text-slate-500">
                  {t("templates.carrySchools", {
                    count: carryPlan.moves.length,
                    names: carryPlan.moves
                      .slice(0, 3)
                      .map((row) => row.organization_name)
                      .join(", "),
                  })}
                  {carryPlan.inherited_count > 0 &&
                    ` ${t("templates.carryInherited", { count: carryPlan.inherited_count })}`}
                  {carryPlan.skipped_count > 0 &&
                    ` ${t("templates.carrySkipped", { count: carryPlan.skipped_count })}`}
                </span>
              )}
            </span>
          </label>
        </ConfirmModal>
      )}
    </div>
  );
}
